import { PartialMetadata, PartialRecord, ReferenceTitles } from '@stylehaven/components';

import { cloneDeep, isEqual, isString, set } from 'lodash-es';

import { GenericOverviewItem } from '../../entities/common/GenericOverviewItem.entity';
import { ReasonCode } from '../../entities/common/reason-code.enum';
import { SubjectRejection } from '../../entities/common/subject-rejection.entity';
import { Fragment } from './fragment/fragment.entity';
import { DottedKey } from './metadata-field.entity';
import {
    ContextHolder,
    ContextMetadata,
    FragmentId,
    Identifier,
    MediaObjectId,
    Metadata,
    RecordType,
    Rejection,
    RelationType
} from './metadata.entity';
import { Comparable, Identifiable } from './misc/comparable.interface';
import { Trackable } from './misc/trackable.interface';
import { RecordStatus } from './record-status.type';

export interface ApiFragmentList {
    readonly NrOfResults: number;
    readonly StartIndex: number;
    readonly TotalNrOfResults: number;
    readonly Results: Array<Metadata>;
}

export class ApiFragment implements Fragment, Comparable, Trackable, ContextHolder, Identifiable, GenericOverviewItem {
    constructor(public readonly metadata: Metadata) {}

    getId(): string {
        return this.getRecordId();
    }

    getIdentifier(): Identifier {
        return this.getRecordId();
    }

    getFragmentId(): FragmentId {
        return this.metadata.Internal?.FragmentId;
    }

    getMediaObjectId(): MediaObjectId {
        return this.metadata.Internal.MediaObjectId;
    }

    getRecordId(): MediaObjectId {
        return this.metadata.Internal.RecordId;
    }

    getVersioningId(): string {
        return this.metadata.Structural.Versioning.Id;
    }

    getVersioningVersion(): string {
        return this.metadata.Structural.Versioning.Version.toString();
    }

    getParentRecordId(): string | undefined {
        return this.metadata.Structural?.ParentRecordId?.toString();
    }

    getDirectParentTitle(): string | null {
        const parentrecordId = this.getParentRecordId()?.toString() || null;
        const referenceTitles: ReferenceTitles | undefined = this.metadata.Structural?.ReferenceTitles;

        if (parentrecordId && referenceTitles) {
            const matchingRecordType: RecordType | null = this.getDirectParentRecordType();

            if (matchingRecordType && referenceTitles.hasOwnProperty(matchingRecordType)) {
                const matchingTitles = referenceTitles[matchingRecordType];

                if (matchingTitles && matchingTitles.length > 0) {
                    return Array.isArray(matchingTitles) ? matchingTitles[matchingTitles.length - 1] : matchingTitles;
                }
            }
        }

        return null;
    }

    getDirectParentRecordType(): RecordType | null {
        const parentrecordId = this.getParentRecordId()?.toString() || null;
        const referenceCodes: ReferenceTitles | undefined = this.metadata.Structural?.ReferenceCodes;
        if (referenceCodes === undefined) {
            return null;
        }

        return parentrecordId
            ? ((Object.entries(referenceCodes).find(([key, values]: [string, string[]]) =>
                  values?.includes(parentrecordId)
              )?.[0] || null) as RecordType)
            : null;
    }

    getTitle(): string {
        return this.metadata.Descriptive.Title;
    }

    getOriginalFileName(): string | null {
        return this.metadata.Descriptive.OriginalFilename || this.getTitle();
    }

    getRecordType(): RecordType {
        return this.metadata.Administrative.RecordType;
    }

    getRecordStatus(): RecordStatus {
        return this.metadata.Administrative.RecordStatus;
    }

    getMainRecordType(): RecordType {
        return this.metadata.Administrative.MainRecordType;
    }

    getReferenceTitle(key: RecordType): string {
        if (!this.metadata.Structural.ReferenceTitles[key]) {
            return '-';
        }
        return this.metadata.Structural.ReferenceTitles[key][0];
    }

    hasReferenceCode(key: RecordType): boolean {
        return this.metadata.Structural.ReferenceCodes[key] !== undefined;
    }

    getReferenceCode(key: RecordType): string {
        if (this.metadata.Structural.ReferenceCodes[key] !== undefined) {
            return this.metadata.Structural.ReferenceCodes[key][0];
        }
        const dynamicValue = this.metadata.Dynamic[`${key}ReferenceCode`];
        if (dynamicValue && isString(dynamicValue)) {
            const parts = dynamicValue.split('/');
            return parts[parts.length - 1];
        }
        return '';
    }

    getStatus(): RecordStatus {
        return this.metadata.Administrative.RecordStatus;
    }

    getReferenceCodes(key: RecordType): string[] {
        return this.metadata.Structural?.ReferenceCodes?.[key] ?? [];
    }

    isNestedStuk(): boolean {
        return this.isStuk() && this.getReferenceCodes(RecordType.Document).length > 0;
    }

    getParentStukTitle(): string | null {
        return this.isNestedStuk() ? this.getDirectParentTitle() : null;
    }

    getParentStukId(): string | null {
        if (!this.isNestedStuk()) {
            return null;
        }

        const ancestors: string[] = this.getReferenceCodes(RecordType.Document);
        const parentRecordId: string | undefined = this.getParentRecordId();
        const direct: string | undefined = ancestors.find(ancestor => parentRecordId?.startsWith(ancestor));

        return direct ?? ancestors[ancestors.length - 1];
    }

    /**
     * Whether this record has children the hierarchical table would reveal — stukken, not representaties.
     *
     * The structural flag on its own is not enough: candidate children are joined by a *relation* rather
     * than counted as structural children, so a dossier whose only stukken are still candidates would
     * report `false` and lose its chevron while having children to show.
     */
    hasChildRecords(): boolean {
        return (
            this.metadata.Structural?.HasNonRepresentationChildren === true ||
            (this.metadata.Structural?.Relations?.['IsCandidateParentOf']?.length ?? 0) > 0
        );
    }

    /**
     * Whether this record is a dossier — read defensively, because a search row is only guaranteed to carry
     * the fields the page asked for and this is used to decide whether a row *needs* an aggregate at all.
     */
    isDossier(): boolean {
        return this.metadata.Administrative?.RecordType === RecordType.Dossier;
    }

    /**
     * Whether this record is a stuk. Read as defensively as {@link isDossier}, and for the same reason: it
     * decides whether a row needs an aggregate looked up for it.
     *
     * A **weesdocument is still not one** — it is a `RecordType` of its own and hangs under no dossier, which is
     * exactly what {@link isNestedStuk} and the tree's aggregates read this for. What the two *do* share is
     * where their formats live, and that question has a predicate of its own: {@link hasRepresentationFormats}.
     * Ask that one, not this one, before reaching for the batched representatie lookup.
     */
    isStuk(): boolean {
        return this.metadata.Administrative?.RecordType === RecordType.Document;
    }

    /** Whether this record is a weesdocument: a document belonging to no dossier, and so never a row in the tree. */
    isOrphanDocument(): boolean {
        return this.metadata.Administrative?.RecordType === RecordType.OrphanDocument;
    }

    /**
     * Whether this record's formats live on its *representaties* — one record each — rather than on itself.
     *
     * True for a stuk and for a weesdocument, which is structurally the same object minus a dossier: it carries
     * representaties of its own and has a `Representaties` tab to show them. This, and not {@link isStuk}, is
     * what the two consumers of the `Formaten` column ask — the overview batches one representatie lookup per
     * page for these records, and the cell reads that answer back.
     *
     * Everything else derives its badge from its own record instead. A **representatie** must: it *is* one
     * format, and pointing the batched lookup at it returns a *wrong* answer rather than a missing one — the
     * lookup groups by parent stuk, finds none, and settles on "ready, no formats".
     */
    hasRepresentationFormats(): boolean {
        return this.isStuk() || this.isOrphanDocument();
    }

    /**
     * Whether this stuk describes a physical original (`Dynamic.DocumentAnalog`). An analog stuk may still have
     * a digital representatie — a scan — so this only says which stukken are *worth asking* about.
     */
    isAnalogDocument(): boolean {
        return this.metadata.Dynamic?.DocumentAnalog === true;
    }

    getRelation(relation: RelationType): FragmentId[] {
        return this.metadata.Structural?.Relations?.[relation] || [];
    }

    getRejections(): SubjectRejection[] | undefined {
        const rejections: Rejection[] | undefined = this.metadata.Administrative.RecordRejections?.Rejection;
        if (!rejections || rejections.length === 0) {
            return undefined;
        }

        return rejections.map(rejection => ({
            User: rejection.User,
            Time: rejection.Date,
            Motivation: rejection.Motivation ?? '',
            SystemCode: rejection.Reason as ReasonCode
        }));
    }

    getField<T>(key: DottedKey | string): T {
        return key.split('.').reduce((metadata: any, key: string): any => {
            if (metadata == undefined) {
                return metadata;
            } else if (Array.isArray(metadata)) {
                return metadata.map(metadata => metadata[key]);
            } else {
                return metadata[key];
            }
        }, this.metadata);
    }

    setField<T>(key: DottedKey | string, value: T): ApiFragment {
        set(this.metadata, key, value);
        return this;
    }

    equals(other: any): boolean {
        return isEqual(this, other);
    }

    copy(): ApiFragment {
        return new ApiFragment(cloneDeep(this.metadata));
    }

    trackBy(): string {
        return this.getMediaObjectId();
    }

    isNavigable(): boolean {
        return !(this.getRecordType() === RecordType.Representation && this.metadata.Structural.ParentRecordId == null);
    }

    allowedByContext(contextAction: keyof ContextMetadata | undefined): boolean {
        if (contextAction == undefined) {
            return true;
        }
        const contextElement = this.metadata.Context[contextAction] as boolean | undefined;
        if (contextElement == undefined) {
            return false;
        }
        return contextElement;
    }

    get Context(): ContextMetadata {
        return this.metadata.Context;
    }

    toPartialRecord(): PartialRecord {
        return new PartialRecord(this.metadata as unknown as PartialMetadata);
    }
}
